import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useAccounts } from "@/contexts/account-context";
import { isForceOnboardingEnabled } from "@/dev/types/inspector-types";

const DEBUG = process.env.NODE_ENV === "development";

// A simple loading spinner
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" />
  </div>
);

const IndexPage: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: isUserLoaded } = useUser();
  const {
    accounts,
    isLoading: isAccountsLoading,
    activeDexieId,
  } = useAccounts();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (DEBUG) console.log("Index Mounts. Initial States:", { isUserLoaded });
  }, [isUserLoaded]);

  useEffect(() => {
    // Prevent navigation loops
    if (isRedirecting || !isUserLoaded) return;

    const performRedirect = (path: string) => {
      if (DEBUG) console.log(`Redirecting to: ${path}`);
      setIsRedirecting(true);
      navigate(path, { replace: true });
    };

    if (isSignedIn) {
      // --- SIGNED-IN USER LOGIC ---
      if (DEBUG)
        console.log("Index Logic Check (Signed In):", {
          isAccountsLoading,
          accountsCount: accounts?.length,
          activeDexieId,
        });

      // Check for force onboarding mode (dev only)
      if (isForceOnboardingEnabled()) {
        if (DEBUG) console.log("Force onboarding mode enabled - redirecting to onboarding");
        performRedirect("/onboarding/start");
        return;
      }

      // Clear any stale onboarding state from localStorage
      try {
        localStorage.removeItem("onboarding_last_step");
        localStorage.removeItem("onboarding_status");
      } catch (e) {
        console.warn("Could not clear onboarding state from localStorage", e);
      }

      if (isAccountsLoading) {
        if (DEBUG)
          console.log("User signed in, waiting for accounts to load...");
        return;
      }

      if (accounts && accounts.length > 0 && activeDexieId) {
        performRedirect("/settings");
      } else if (accounts && accounts.length > 0 && !activeDexieId) {
        performRedirect("/settings");
      } else {
        performRedirect("/setup");
      }
    } else {
      // --- SIGNED-OUT USER LOGIC (SYNCHRONOUS) ---
      let lastStep: string | null = null;
      let isComplete = false;
      try {
        lastStep = localStorage.getItem("onboarding_last_step");
        isComplete = localStorage.getItem("onboarding_status") === "complete";
      } catch (e) {
        console.warn("Could not read onboarding state from localStorage", e);
      }

      if (DEBUG)
        console.log("Index Logic Check (Signed Out):", {
          lastStep,
          isComplete,
        });

      if (lastStep) {
        performRedirect(lastStep);
      } else {
        performRedirect("/onboarding/get-started");
      }
    }
  }, [
    isSignedIn,
    isUserLoaded,
    accounts,
    activeDexieId,
    isAccountsLoading,
    navigate,
    isRedirecting,
  ]);

  // Render a loading spinner while logic is processing
  return <LoadingSpinner />;
};

export default IndexPage;
